import React from 'react';
import { getLabel, getLabelStyle } from './widgetLabelHelpers';
import './AutoFindingsWidgets.css';

/**
 * AUTO FINDINGS WIDGETS
 * These widgets match the auto-generated sections from template1.js and template2.js
 * They automatically populate data without requiring user configuration
 */

// ============================================================================
// 1. NESTED RECTANGLE FINDINGS WIDGET (Template1 Signature Visual)
// ============================================================================

/**
 * Nested Rectangle Findings Widget
 * This is the signature visual from template1.js - colored boxes with findings
 * Displays findings in the classic template1 nested rectangle format
 */
export const NestedRectangleFindingsWidget = ({ data, config = {}, style = {} }) => {
  const widgetType = 'nested-rectangle-findings';
  const {
    showScore = true,
    showDots = true
  } = config;

  // Customizable Title
  const title = getLabel(config, widgetType, 'title') || config.title || 'Security Findings';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);

  const safeData = data || {};
  const exportDataSource = safeData.exportData || safeData;
  const findings = [];
  const category = config.category || null;

  // Extract findings from exportData
  if (exportDataSource && typeof exportDataSource === 'object') {
    Object.entries(exportDataSource).forEach(([categoryName, categoryData]) => {
      if (category && categoryName !== category) return;
      if (!Array.isArray(categoryData) || categoryData.length < 2) return;

      const rectangles = categoryData[0];
      const categoryScore = categoryData[1];

      if (Array.isArray(rectangles)) {
        rectangles.forEach(rectangle => {
          findings.push({
            ...rectangle,
            category: categoryName,
            categoryScore
          });
        });
      }
    });
  }

  return (
    <div className="nested-rectangle-findings-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      <div className="nested-rectangles-container">
        {findings.map((finding, idx) => (
          <div key={idx} className={`nested-rectangle status-${finding.color || 'gray'}`}>
            {/* Three dots indicator */}
            {showDots && (
              <div className="three-dots">
                <span className={`dot ${finding.color || 'gray'}`}></span>
                <span className={`dot ${finding.color || 'gray'}`}></span>
                <span className={`dot ${finding.color || 'gray'}`}></span>
              </div>
            )}

            {/* Blue vertical bars */}
            <div className="vertical-bars">
              <div className="bar"></div>
              <div className="bar"></div>
            </div>

            {/* Outer text (finding title) */}
            <div className="outer-text">
              {finding.outerText}
            </div>

            {/* Inner list items (details) */}
            {finding.innerListItems && finding.innerListItems.length > 0 && (
              <div className="inner-list">
                {finding.innerListItems.map((item, i) => (
                  <div key={i} className="inner-item">{item}</div>
                ))}
              </div>
            )}

            {/* Score badge */}
            {showScore && finding.score && (
              <div className={`score-badge badge-${finding.color || 'gray'}`}>
                {finding.score}%
              </div>
            )}
          </div>
        ))}
      </div>

      {findings.length === 0 && (
        <div className="no-findings-message">
          No findings available for this category
        </div>
      )}
    </div>
  );
};

// ============================================================================
// 2. COMPANY AT A GLANCE WIDGET
// ============================================================================

/**
 * Company At A Glance Widget
 * Shows overview cards for all scan types with icons and key metrics
 * Matches template1.js "Your Company At A Glance" section
 */
export const CompanyAtAGlanceWidget = ({ data, config = {}, style = {} }) => {
  const widgetType = 'company-at-a-glance';
  const {
    showIcons = true,
    showRiskScore = true,
    showFinancialImpact = true
  } = config;

  const title = getLabel(config, widgetType, 'title') || config.title || 'Your Company At A Glance';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);

  const safeData = data || {};
  const coverDataSource = safeData.coverData || safeData.keyValue || {};
  const exportDataSource = safeData.exportData || safeData;
  const totalPII = safeData.totalPII || 0;

  // Calculate overall risk score
  let totalScore = 0;
  let categoryCount = 0;
  if (exportDataSource && typeof exportDataSource === 'object') {
    Object.entries(exportDataSource).forEach(([category, categoryData]) => {
      if (Array.isArray(categoryData) && categoryData.length >= 2) {
        const score = parseFloat(categoryData[1]) || 0;
        totalScore += score;
        categoryCount++;
      }
    });
  }
  const overallScore = categoryCount > 0 ? Math.round(totalScore / categoryCount) : 0;

  // Calculate financial impact (from template1.js formula)
  const redAndYellow = categoryCount > 0 ? totalScore / categoryCount : 0;
  const financialImpact = Math.round(redAndYellow * 425 * totalPII);

  // Risk color
  const getRiskColor = (score) => {
    if (score >= 70) return '#dc3545'; // red
    if (score >= 40) return '#ffc107'; // yellow
    return '#28a745'; // green
  };

  const getRiskLabel = (score) => {
    if (score >= 70) return 'Critical Risk';
    if (score >= 40) return 'At Risk';
    return 'Acceptable';
  };

  // Extract category metrics
  const categories = [];

  // Active Directory
  if (coverDataSource['Active Directory Scan']) {
    const ad = coverDataSource['Active Directory Scan'];
    categories.push({
      name: 'Active Directory Scan',
      icon: '👥',
      metrics: [
        { label: 'Users', value: ad.AD_Users || ad['AD Users'] || 0 },
        { label: 'Inactive Users', value: ad.orphaned_accounts_in_ad || 0 },
        { label: 'Domain Controllers', value: ad.domain_controllers || 0 },
        { label: 'Computers', value: ad.computers || 0 }
      ]
    });
  }

  // Microsoft 365
  if (coverDataSource['Microsoft 365 Scan']) {
    const ms = coverDataSource['Microsoft 365 Scan'];
    categories.push({
      name: 'Microsoft 365 Scan',
      icon: '☁️',
      metrics: [
        { label: 'Users', value: ms.total_users || 0 },
        { label: 'Inactive Users', value: ms.orphaned_accounts_in_ms || 0 }
      ]
    });
  }

  // Network Scan
  if (coverDataSource['Network Scan']) {
    const network = coverDataSource['Network Scan'];
    const routerCount = Array.isArray(network.device_type_Router)
      ? network.device_type_Router[1]
      : network.device_type_Router || 0;
    categories.push({
      name: 'Network Scan',
      icon: '🌐',
      metrics: [
        { label: 'Devices', value: network.total_devices || routerCount || 0 }
      ]
    });
  }

  // On-Prem Scan
  if (coverDataSource['On-Prem Scan']) {
    const onprem = coverDataSource['On-Prem Scan'];
    categories.push({
      name: 'On-Prem Scan',
      icon: '🖥️',
      metrics: [
        { label: 'Servers', value: onprem.total || 0 }
      ]
    });
  }

  // Localhost Scan
  if (coverDataSource['Localhost Scan']) {
    const localhost = coverDataSource['Localhost Scan'];
    categories.push({
      name: 'Localhost Scan',
      icon: '💻',
      metrics: [
        { label: 'Devices', value: localhost.total_devices || 1 }
      ]
    });
  }

  // PII Scan
  if (coverDataSource['Personally Identifiable Information']) {
    const pii = coverDataSource['Personally Identifiable Information'];
    categories.push({
      name: 'Personally Identifiable Information',
      icon: '🔐',
      metrics: [
        { label: 'Emails', value: pii.total_email || 0 },
        { label: 'Breached', value: pii.breached_email || 0 }
      ]
    });
  }

  return (
    <div className="company-at-a-glance-widget" style={style}>
      {title && <h2 className="glance-widget-title" style={titleStyle}>{title}</h2>}

      {/* Category Cards */}
      <div className="category-cards-grid">
        {categories.map((cat, idx) => (
          <div key={idx} className="category-card">
            {showIcons && (
              <div className="category-icon">{cat.icon}</div>
            )}
            <h4 className="category-name">{cat.name}</h4>
            <div className="category-metrics">
              {cat.metrics.map((metric, i) => (
                <div key={i} className="metric-row">
                  <span className="metric-label">{metric.label}:</span>
                  <span className="metric-value">{metric.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Risk Score Box */}
      {showRiskScore && (
        <div className="risk-score-box" style={{ borderColor: getRiskColor(overallScore) }}>
          <h4>Overall Risk Score</h4>
          <div className="risk-score-value" style={{ color: getRiskColor(overallScore) }}>
            {overallScore}%
          </div>
          <div className="risk-label">{getRiskLabel(overallScore)}</div>
        </div>
      )}

      {/* Financial Impact Box */}
      {showFinancialImpact && financialImpact > 0 && (
        <div className="financial-impact-box">
          <h4>Estimated Financial Impact</h4>
          <div className="financial-value">${financialImpact.toLocaleString()}</div>
          <small>Based on risk analysis and PII exposure</small>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// 3. CATEGORY SCORE BAR WIDGET
// ============================================================================

/**
 * Category Score Bar Widget
 * Shows a horizontal colored bar indicating risk level
 * Matches the score bar from template1.js
 */
export const CategoryScoreBarWidget = ({ data, config = {}, style = {} }) => {
  const widgetType = 'category-score-bar';
  const {
    category = null,
    showLabel = true,
    showPercentage = true
  } = config;

  const title = getLabel(config, widgetType, 'title') || config.title || 'Risk Level';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);

  const safeData = data || {};
  const exportDataSource = safeData.exportData || safeData;
  let score = 0;
  let categoryName = category || 'Overall';

  // Get score for specific category or calculate overall
  if (category && exportDataSource[category]) {
    if (Array.isArray(exportDataSource[category]) && exportDataSource[category].length >= 2) {
      score = parseFloat(exportDataSource[category][1]) || 0;
    }
  } else if (!category) {
    // Calculate overall score
    let totalScore = 0;
    let count = 0;
    Object.entries(exportDataSource || {}).forEach(([cat, catData]) => {
      if (Array.isArray(catData) && catData.length >= 2) {
        totalScore += parseFloat(catData[1]) || 0;
        count++;
      }
    });
    score = count > 0 ? totalScore / count : 0;
  }

  const getColor = (s) => {
    if (s >= 70) return '#dc3545';
    if (s >= 40) return '#ffc107';
    return '#28a745';
  };

  const getRiskLabel = (s) => {
    if (s >= 70) return 'Critical';
    if (s >= 40) return 'Warning';
    return 'Good';
  };

  return (
    <div className="category-score-bar-widget" style={style}>
      {title && <h4 className="score-bar-title" style={titleStyle}>{title}</h4>}

      <div className="score-bar-container">
        <div className="score-bar-bg">
          <div
            className="score-bar-fill"
            style={{
              width: `${score}%`,
              backgroundColor: getColor(score)
            }}
          ></div>
        </div>

        <div className="score-bar-info">
          {showPercentage && (
            <span className="score-percentage" style={{ color: getColor(score) }}>
              {Math.round(score)}%
            </span>
          )}
          {showLabel && (
            <span className="score-label">{getRiskLabel(score)}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// 4. FINANCIAL IMPACT WIDGET
// ============================================================================

/**
 * Financial Impact Widget
 * Calculates and displays financial risk based on score and PII
 * Formula from template1.js: redAndYellow * 425 * PII
 */
export const FinancialImpactWidget = ({ data, config = {}, style = {} }) => {
  const widgetType = 'financial-impact';
  const {
    showFormula = false,
    currencySymbol = '$'
  } = config;

  const title = getLabel(config, widgetType, 'title') || config.title || 'Estimated Financial Impact';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);

  const safeData = data || {};
  const exportDataSource = safeData.exportData || safeData;
  const totalPII = safeData.totalPII || 0;

  // Calculate average risk score
  let totalScore = 0;
  let categoryCount = 0;
  Object.entries(exportDataSource || {}).forEach(([category, categoryData]) => {
    if (Array.isArray(categoryData) && categoryData.length >= 2) {
      const score = parseFloat(categoryData[1]) || 0;
      totalScore += score;
      categoryCount++;
    }
  });

  const redAndYellow = categoryCount > 0 ? totalScore / categoryCount : 0;
  const financialImpact = Math.round(redAndYellow * 425 * totalPII);

  const getRiskLevel = () => {
    if (financialImpact > 100000) return { level: 'Severe', color: '#dc3545' };
    if (financialImpact > 50000) return { level: 'High', color: '#ffc107' };
    if (financialImpact > 10000) return { level: 'Moderate', color: '#17a2b8' };
    return { level: 'Low', color: '#28a745' };
  };

  const riskLevel = getRiskLevel();

  return (
    <div className="financial-impact-widget" style={style}>
      {title && <h3 className="financial-widget-title" style={titleStyle}>{title}</h3>}

      <div className="financial-impact-card" style={{ borderColor: riskLevel.color }}>
        <div className="financial-amount" style={{ color: riskLevel.color }}>
          {currencySymbol}{financialImpact.toLocaleString()}
        </div>

        <div className="financial-risk-level" style={{ color: riskLevel.color }}>
          {riskLevel.level} Risk
        </div>

        <div className="financial-breakdown">
          <div className="breakdown-item">
            <span className="breakdown-label">Avg Risk Score:</span>
            <span className="breakdown-value">{Math.round(redAndYellow)}%</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">PII Records:</span>
            <span className="breakdown-value">{totalPII}</span>
          </div>
        </div>

        {showFormula && (
          <div className="financial-formula">
            <small>Formula: Risk Score × 425 × PII Count</small>
          </div>
        )}
      </div>

      <div className="financial-note">
        <small>This is an estimated impact based on average data breach costs and exposed PII records.</small>
      </div>
    </div>
  );
};

// ============================================================================
// 5. CATEGORY DESCRIPTION WIDGET
// ============================================================================

/**
 * Category Description Widget
 * Shows "Why is it important?" section for a category
 * Uses descriptions from template1.js getDescription() function
 */
export const CategoryDescriptionWidget = ({ data, config = {}, style = {} }) => {
  const widgetType = 'category-description';
  const {
    category = 'Active Directory Scan',
    showTitle = true
  } = config;

  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);

  // Descriptions from template1.js getDescription() function
  const descriptions = {
    'Personally Identifiable Information': 'The goal of a PII scan is to detect any personally identifiable information on your network. PII can include Social Security Numbers, credit card numbers, bank account numbers, passport numbers, driver\'s license numbers, and more. Exposure of this data can lead to identity theft, fraud, and regulatory fines.',

    'Localhost Scan': 'The localhost scan examines the local machine for security vulnerabilities, including outdated software, open ports, missing patches, and system misconfigurations. This helps identify risks on individual workstations that could be exploited by attackers.',

    'Network Scan': 'The network scan discovers and analyzes all devices connected to your network, including routers, switches, printers, IoT devices, and workstations. It identifies open ports, running services, and potential security weaknesses across your network infrastructure.',

    'Active Directory Scan': 'Active Directory is the centralized authentication and authorization service in Windows environments. This scan examines user accounts, computers, groups, permissions, and policies to identify security risks such as inactive accounts, weak passwords, excessive permissions, and configuration issues.',

    'Microsoft 365 Scan': 'Microsoft 365 (formerly Office 365) is a cloud-based productivity platform. This scan examines user accounts, licenses, security settings, and compliance configurations to ensure your cloud environment is properly secured against unauthorized access and data leaks.',

    'On-Prem Scan': 'The on-premises server scan examines physical and virtual servers in your environment, checking for outdated operating systems, missing patches, insecure configurations, and compliance issues. Servers often contain critical business data and require strong security controls.'
  };

  const description = descriptions[category] || 'No description available for this category.';

  return (
    <div className="category-description-widget" style={style}>
      {showTitle && <h3 className="description-title" style={titleStyle}>Why is {category} important?</h3>}

      <div className="description-content">
        <p>{description}</p>
      </div>
    </div>
  );
};

// Export all widgets
export default {
  NestedRectangleFindingsWidget,
  CompanyAtAGlanceWidget,
  CategoryScoreBarWidget,
  FinancialImpactWidget,
  CategoryDescriptionWidget
};
